import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Star, Eye, EyeOff, User, Calendar, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/use-toast';
import { formatDistanceToNow } from 'date-fns';

interface Review {
  id: string;
  customer_name: string;
  customer_email?: string | null;
  review_text: string;
  rating?: number | null;
  is_verified?: boolean | null;
  is_visible?: boolean | null;
  created_at?: string | null;
  service_id?: string | null;
  service_request_id?: string | null;
}

interface ReviewsManagementProps {
  userId: string;
}

export const ReviewsManagement: React.FC<ReviewsManagementProps> = ({ userId }) => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingReviews, setUpdatingReviews] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (userId) {
      fetchReviews();
    }
  }, [userId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('service_provider_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        toast({
          title: t('common.error'),
          description: "Failed to load reviews",
          variant: "destructive",
        });
        return;
      }

      setReviews((data as any[]) || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast({
        title: t('common.error'),
        description: "Failed to load reviews",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (reviewId: string, currentVisibility: boolean) => {
    try {
      setUpdatingReviews(prev => new Set(prev).add(reviewId));
      
      const { error } = await supabase
        .from('reviews')
        .update({ is_visible: !currentVisibility } as any)
        .eq('id', reviewId);

      if (error) {
        console.error('Error updating review visibility:', error);
        toast({
          title: t('common.error'),
          description: "Failed to update review visibility",
          variant: "destructive",
        });
        return;
      }

      // Update local state
      setReviews(prev => 
        prev.map(review => 
          review.id === reviewId 
            ? { ...review, is_visible: !currentVisibility }
            : review
        )
      );

      toast({
        title: t('common.success'),
        description: !currentVisibility ? t('reviews.reviewShown') : t('reviews.reviewHidden'),
      });
    } catch (error) {
      console.error('Error updating review visibility:', error);
      toast({
        title: t('common.error'),
        description: "Failed to update review visibility",
        variant: "destructive",
      });
    } finally {
      setUpdatingReviews(prev => {
        const newSet = new Set(prev);
        newSet.delete(reviewId);
        return newSet;
      });
    }
  };

  const renderStars = (rating: number | null | undefined) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-sm text-gray-600 ml-1">{rating}/5</span>
      </div>
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gebeya-pink mb-4"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('reviews.noReviews')}</h3>
          <p className="text-gray-600 mb-6">
            {t('reviews.noReviewsDescription')}
          </p>
          <p className="text-sm text-gray-500">
            {t('reviews.manageVisibility')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const visibleReviews = reviews.filter(review => review.is_visible);
  const hiddenReviews = reviews.filter(review => !review.is_visible);

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.totalReviews')}</p>
                <p className="text-2xl font-bold text-gray-900">{reviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.visibleReviews')}</p>
                <p className="text-2xl font-bold text-gray-900">{visibleReviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <EyeOff className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-600">{t('reviews.hiddenReviews')}</p>
                <p className="text-2xl font-bold text-gray-900">{hiddenReviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <Card key={review.id} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  {/* Customer Avatar */}
                  <div className="w-10 h-10 bg-gradient-to-r from-gebeya-pink to-gebeya-orange rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {getInitials(review.customer_name)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{review.customer_name}</h4>
                      {review.is_verified && (
                        <Badge variant="outline" className="text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          {t('reviews.verified')}
                        </Badge>
                      )}
                      <Badge variant={review.is_visible ? "default" : "secondary"} className="text-xs">
                        {review.is_visible ? t('reviews.visibleReviews') : t('reviews.hiddenReviews')}
                      </Badge>
                    </div>
                    
                    {/* Rating */}
                    {review.rating && (
                      <div className="mb-3">
                        {renderStars(review.rating)}
                      </div>
                    )}
                    
                    {/* Review Text */}
                    <p className="text-gray-700 mb-3 leading-relaxed">
                      {review.review_text}
                    </p>
                    
                    {/* Footer */}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {review.created_at && formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                      </div>
                      {review.customer_email && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {review.customer_email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Visibility Toggle */}
                <div className="flex items-center space-x-2 ml-4">
                  <label htmlFor={`visibility-${review.id}`} className="text-sm font-medium text-gray-700">
                    {review.is_visible ? t('reviews.visibleReviews') : t('reviews.hiddenReviews')}
                  </label>
                  <Switch
                    id={`visibility-${review.id}`}
                    checked={review.is_visible || false}
                    onCheckedChange={() => toggleVisibility(review.id, review.is_visible || false)}
                    disabled={updatingReviews.has(review.id)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}; 